/**
 * Round 96 — useTaskCompletion hook.
 *
 * A single, small helper that any bot save handler can call after
 * a successful save to close the Round-94 workflow loop:
 *
 *   1. POSTs `/api/paygbot/tasks/complete` with the task_id from
 *      the deep-link query params (Round 95).
 *   2. Shows a positive completion toast (or the fallback "You're
 *      finished for today" line when no next step is supplied).
 *   3. Dispatches a `paygbot:task-completed` DOM event so the
 *      Dashboard's DailyMissionCard can refresh its Today's Mission
 *      + Business Health without polling.
 *   4. Navigates the user to `return_to` when supplied.
 *
 * The hook is a no-op when no `task_id` query param is present,
 * so calling it unconditionally from a bot save is safe — pre-
 * Round-94 bot pages behave identically.
 *
 * Idempotency is enforced backend-side; this hook does not
 * duplicate-suppress locally.
 */
import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/utils/api';
import useDeepLinkParams from './useDeepLinkParams';

/**
 * @returns {{
 *   completeIfDeepLinked: (evidence?: object,
 *     recommendedNextStep?: object) => Promise<object|null>,
 *   hasDeepLink: boolean,
 * }}
 */
export default function useTaskCompletion() {
  const navigate = useNavigate();
  const deepLink = useDeepLinkParams();
  const firedRef = useRef(false);

  const completeIfDeepLinked = useCallback(async (
    evidence,
    recommendedNextStep,
  ) => {
    if (!deepLink.task_id) return null;
    // Guard against a caller firing twice within a single mount
    // (e.g. a double-clicked save button). Backend is idempotent
    // anyway; this just avoids the second network trip + toast.
    if (firedRef.current) return null;
    firedRef.current = true;

    try {
      const resp = await api.post(
        '/api/paygbot/tasks/complete',
        {
          task_id: deepLink.task_id,
          evidence: evidence || undefined,
          recommended_next_step: recommendedNextStep || undefined,
        },
      );
      const data = resp?.data || {};

      // Positive completion messaging.
      if (data.ok && (data.completed_now || data.was_already_complete)) {
        toast.success(
          data.message
            || "Today's most important work is complete.",
          {
            description: data.next_step && data.next_step.label
              ? `Next: ${data.next_step.label}`
              : undefined,
            duration: 6000,
          },
        );
      }

      // Signal the Dashboard to refresh its Today's Mission +
      // Business Health without polling.
      try {
        window.dispatchEvent(new CustomEvent('paygbot:task-completed', {
          detail: {
            task_id: deepLink.task_id,
            source: data.source || null,
            business_health_should_refresh: Boolean(data.completed_now),
            next_step: data.next_step || null,
          },
        }));
      } catch (_) { /* non-fatal in test envs */ }

      // Route back to `return_to` when supplied — completes the
      // TurboTax-style loop.
      if (deepLink.return_to) {
        // Small delay so the customer sees the toast before
        // the route switch.
        setTimeout(() => {
          try { navigate(deepLink.return_to); } catch (_) { /* noop */ }
        }, 900);
      }

      return data;
    } catch (err) {
      // Non-fatal — the customer's save already succeeded. We
      // only fail the completion loop soft-ly.
      console.warn('[useTaskCompletion] completion failed', err);
      return null;
    }
  }, [deepLink.task_id, deepLink.return_to, navigate]);

  return {
    completeIfDeepLinked,
    hasDeepLink: Boolean(deepLink.task_id),
  };
}
